import { ENV_TEACHR, ENV_VERIFIED_TRAINER, isBrandActive } from "../config/environment.config";

/**
 * Montants présentés aux coachs Verified Trainer (cahier des charges « TVA + 21 % », 1er novembre 2026).
 *
 * Vocabulaire :
 * - prix total TTC (100 %) : montant payé par le client + crédit d'impôt, facturé par Verified Trainer SAP ;
 * - montant payé par le client (50 %) : la moitié du prix total, grâce à l'avance immédiate ;
 * - part du coach : 79 % du prix total hors TVA (TVA du territoire du client : 20 % en métropole, 8,5 % en
 *   Guadeloupe, Martinique et Réunion, 0 en Guyane et à Mayotte), facturée à Verified Trainer ; la TVA d'un
 *   coach assujetti s'ajoute par-dessus, au taux du territoire, comme sur sa facture (TeachrVatCalculator) ;
 * - commissions (21 %) : 8 % au parrain direct, 2 % au parrain du parrain, 11 % à Verified Trainer, sur le HT.
 * Pour 150 € TTC en métropole : 125 € HT, 98,75 € HT pour le coach, 118,50 € TVA comprise s'il est assujetti.
 * Client à la Réunion, coach à 20 % : 138,25 € HT, 109,22 € HT pour le coach, 131,06 € TVA comprise.
 *
 * Les profs Teach'r passés par la coopérative Verified Trainer SAP suivent la même règle avec leur propre taux
 * de frais (12,5 %). Les cours déjà déclarés gardent les montants enregistrés à la déclaration (priceCf,
 * teachrVat) : ils ne sont jamais recalculés avec les taux courants.
 *
 * L'API renvoie le prix total de chaque cours (priceCf.priceWithFees) mais des montants mensuels nets
 * (teachr_price × heures, soit prix total × part du coach) : ces derniers sont ramenés en TTC avec le taux de
 * frais du coach, ce qui est l'hypothèse que faisait déjà l'application pour calculer le plafond.
 */

/** Date de la répartition « TVA + 21 % » : cours déclarés à partir de ce jour. */
export const VT_NEW_SPLIT_FROM = "2026-11-01";

/** Taux de TVA du territoire d'un code postal, en pourcentage (même barème que le backend). */
export const vtTerritorialVatRate = (zip?: string | null): number => {
  const department = String(parseInt(String(zip ?? "").slice(0, 3), 10));
  if (["971", "972", "974"].includes(department)) return 8.5;
  if (["973", "976"].includes(department)) return 0;
  return 20;
};

/** SIREN de Verified Trainer SAP (SIRET 943 371 484 00016), la coopérative qui déclare les cours à l'URSSAF. */
export const VT_SAP_SIREN = "943371484";

/** Plafond mensuel par client, en prix total TTC. */
export const VT_MONTHLY_CEILING_TTC = 1980;

/** Marque Verified Trainer active (fixée au build via REACT_APP_BRAND). */
export const isVerifiedTrainer = (): boolean => isBrandActive(ENV_VERIFIED_TRAINER);

/**
 * Le coach est facturé par la coopérative Verified Trainer SAP : toute la marque Verified Trainer, et chez
 * Teach'r les profs passés par la coopérative (champ scoop renseigné, même condition que le backend).
 */
export const isCooperativeCoach = (teachr: { scoop?: string | null } | null | undefined): boolean =>
  isVerifiedTrainer() || (isBrandActive(ENV_TEACHR) && !!teachr?.scoop);

type TeachrRates = {
  defaultFees?: string | number | null;
  tauxTva?: string | number | null;
} | null | undefined;

const toNumber = (value: unknown): number => {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(n) ? n : 0;
};

/** Taux de frais du coach (0.21 chez Verified Trainer depuis le 1er novembre 2026, 0.125 chez Teach'r). */
export const vtFeesRate = (teachr: TeachrRates): number => toNumber(teachr?.defaultFees);

/** Taux de TVA du coach, en pourcentage (0, 10 ou 20). */
export const vtTvaRate = (teachr: TeachrRates): number => toNumber(teachr?.tauxTva);

/** Prix total TTC d'un cours : prix horaire payé par le client × 2 × durée en heures. */
export const vtTtcFromHourlyPaid = (hourlyPaid: unknown, hours: unknown): number =>
  toNumber(hourlyPaid) * 2 * toNumber(hours);

/** Montant payé par le client : la moitié du prix total TTC. */
export const vtClientShare = (ttc: number): number => ttc / 2;

/**
 * Montant net enregistré côté API pour un prix total : prix total × part du coach (teachr_price × heures).
 * C'est une clé de conversion avec les montants mensuels de l'API, pas ce que le coach reçoit (voir vtCoachShareTtc).
 */
export const vtNetFromTtc = (ttc: number, teachr: TeachrRates): number => ttc * (1 - vtFeesRate(teachr));

/** Prix total hors TVA du territoire du client (125 € pour 150 € en métropole). */
export const vtHtFromTtc = (ttc: number, territorialRate = 20): number => ttc / (1 + territorialRate / 100);

/** Commissions (parrains et Verified Trainer) prélevées sur le prix total hors TVA : 21 % du HT. */
export const vtCommissions = (ttc: number, teachr: TeachrRates, territorialRate = 20): number =>
  vtHtFromTtc(ttc, territorialRate) * vtFeesRate(teachr);

/** Le coach est assujetti à la TVA : taux non nul dans ses paramètres de facturation. */
export const vtIsSubjectToVat = (teachr: TeachrRates): boolean => vtTvaRate(teachr) > 0;

/** Part hors TVA du coach : 79 % du prix total hors TVA (98,75 € pour 150 € en métropole). */
export const vtCoachShareHt = (ttc: number, teachr: TeachrRates, territorialRate = 20): number =>
  vtHtFromTtc(ttc, territorialRate) * (1 - vtFeesRate(teachr));

/** TVA du coach, ajoutée par-dessus sa part HT à son propre taux de profil s'il est assujetti (0 sinon). */
export const vtCoachVat = (ttc: number, teachr: TeachrRates, territorialRate = 20): number =>
  vtIsSubjectToVat(teachr) ? vtCoachShareHt(ttc, teachr, territorialRate) * (vtTvaRate(teachr) / 100) : 0;

/**
 * Ce que le coach reçoit, tel que sa facture adressée à Verified Trainer le porte : sa part HT, plus sa TVA s'il
 * est assujetti. Pour 150 € en métropole : 98,75 € sans TVA, 118,50 € avec. Pour un cours déjà déclaré, utiliser
 * vtCourseRemuneration, qui lit les montants enregistrés.
 */
export const vtCoachShareTtc = (ttc: number, teachr: TeachrRates, territorialRate = 20): number =>
  vtCoachShareHt(ttc, teachr, territorialRate) + vtCoachVat(ttc, teachr, territorialRate);

/** Part hors TVA contenue dans un montant TTC du coach (TVA à son taux de profil). */
export const vtHtFromCoachTtc = (coachTtc: number, teachr: TeachrRates): number =>
  coachTtc / (1 + vtTvaRate(teachr) / 100);

/** TVA contenue dans un montant TTC du coach (0 s'il n'est pas assujetti). */
export const vtTvaIncluded = (coachTtc: number, teachr: TeachrRates): number =>
  coachTtc - vtHtFromCoachTtc(coachTtc, teachr);

/** Libellé de la rémunération : précise « TTC » quand le coach est assujetti à la TVA. */
export const vtRemunerationLabel = (teachr: TeachrRates, base = "Rémunération"): string =>
  vtIsSubjectToVat(teachr) ? `${base} TTC` : base;

/**
 * Montants d'un cours déjà déclaré, tels que renvoyés par l'API (priceCf). Ils ont été enregistrés au moment
 * de la déclaration : ils ne bougent pas si le coach change ensuite son taux de TVA, contrairement à un
 * calcul fait à l'affichage avec les taux courants. La facture est générée à partir des mêmes valeurs.
 */
type ApiCourse = {
  priceCf?: { priceWithFees?: unknown; priceNoFees?: unknown } | null;
  amount?: { priceWithFees?: unknown; priceNoFees?: unknown } | null;
  /** Part du coach enregistrée sur la charge à la déclaration (hors TVA, TVA, taux), null avant la migration */
  teachrVat?: { amountHt?: unknown; vatAmount?: unknown; vatRate?: unknown } | null;
} | null | undefined;

/** Prix total TTC enregistré du cours (payé par le client + crédit d'impôt). */
export const vtCoursePrixTotal = (course: ApiCourse): number =>
  toNumber(course?.priceCf?.priceWithFees ?? course?.amount?.priceWithFees);

/**
 * Rémunération enregistrée du cours, telle que sa facture la porte : part HT plus TVA enregistrées à la déclaration
 * (teachrVat). À défaut (cours antérieur à la migration), teachr_price × heures.
 */
export const vtCourseRemuneration = (course: ApiCourse): number => {
  const vat = course?.teachrVat;
  if (vat && vat.amountHt != null && vat.amountHt !== "") {
    return toNumber(vat.amountHt) + toNumber(vat.vatAmount);
  }
  return toNumber(course?.priceCf?.priceNoFees ?? course?.amount?.priceNoFees);
};

/** Prix total TTC à partir d'un montant net renvoyé par l'API (teachr_price × heures). */
export const vtTtcFromNet = (net: unknown, teachr: TeachrRates): number => {
  const ratio = 1 - vtFeesRate(teachr);
  return ratio > 0 ? toNumber(net) / ratio : toNumber(net);
};
